<template>
  <div>
    <v-toolbar
      class="machines-group"
      color="transparent"
      flat
    >
      <v-btn
        class="elevation-0"
        color="primary"
        @click="onAddMachineGroupClick"
      >
        <v-icon left>
          add
        </v-icon>
        {{ $t('Common.Button.Add') }}
      </v-btn>
    </v-toolbar>

    <v-divider />

    <v-container
      class="pa-0"
      fluid
    >
      <v-row dense>
        <v-col cols="12">
          <v-data-table
            :footer-props="dataTable.footerProps"
            :headers="machineGroupsHeaders"
            :height="dataTable.resize.bodyHeight"
            :items="dataTable.data"
            :loading="dataTable.loading"
            :loading-text="dataTable.text.loadingText"
            :no-data-text="dataTable.text.noDataText"
            :no-results-text="dataTable.text.noResultsText"
            :options.sync="dataTable.options"
            class="elevation-0"
            fixed-header
            hide-default-footer
          >
            <template #[`item.Id`]="{item}">
              {{ item.Id }}
            </template>

            <template #[`item.Name`]="{item}">
              <div :class="{'caption': $vuetify.breakpoint.xsOnly}">
                {{ item.Name }}
              </div>
            </template>

            <template #[`item.Description`]="{item}">
              <div :class="{'caption': $vuetify.breakpoint.xsOnly}">
                {{ item.Description }}
              </div>
            </template>

            <template #[`item.MachinesCount`]="{item}">
              <v-chip
                class="justify-center"
                color="grey"
                label
                small
                text-color="white"
              >
                {{ item.MachinesCount || 0 }}
              </v-chip>
            </template>

            <template #[`item.Type`]="{item}">
              <v-icon v-if="item.Type === MachineGroupTypeEnum.PRINT">
                mdi-printer
              </v-icon>

              <v-icon v-if="item.Type === MachineGroupTypeEnum.VOIP">
                mdi-phone
              </v-icon>
            </template>

            <template #[`item.Status`]="{item}">
              <v-switch
                v-model="item.Status"
                :disabled="!userCanManage || (!userIsSuperAdmin && item.Type === MachineGroupTypeEnum.VOIP)"
                :label="item.Status ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')"
                class="ma-0"
                color="success"
                hide-details
                inset
                @change="onToggleMachineGroupStatus(item)"
              />
            </template>

            <template #[`item.Actions`]="{item}">
              <v-btn
                v-if="userCanDelete && userCanManage && parseInt(item.MachinesCount) === 0"
                :class="$t('Settings.Permission.Toolbar.Button.Delete.Color')"
                :disabled="parseInt(item.MachinesCount) > 0 || (!userIsSuperAdmin && item.Type === MachineGroupTypeEnum.VOIP) || item.Loading"
                :loading="item.loading"
                class="white--text elevation-2 ma-1"
                icon
                small
                @click="onDeleteMachineGroupClick(item)"
              >
                <v-icon small>
                  delete
                </v-icon>
              </v-btn>

              <v-btn
                v-if="userCanAdd && userCanManage"
                :disabled="!userIsSuperAdmin && item.Type === MachineGroupTypeEnum.VOIP"
                class="cyan white--text elevation-2 ma-1"
                icon
                small
                @click="onEditMachineGroupClick(item)"
              >
                <v-icon small>
                  edit
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <machine-group-add-dialog
        :item-to-edit="selectedMachineGroup"
        :visible.sync="machineGroupAddDialogVisible"
      />
    </v-container>
  </div>
</template>

<script>

import AppData from '@/mixins/appdata.js'
import Auth from '@/mixins/auth.js'
import DataTable from '@/mixins/data-table/dataTable.js'
import {clone} from '@/lib/helper/helper.js'
import MachineGroupAddDialog from '@/components/settings/printers/MachineGroupAddDialog.vue'
import MachineGroupTypeEnum from '@/api/Enums/MachineGroupTypeEnum.js'

export default {
  name      : 'MachineGroupTab',
  components: {MachineGroupAddDialog},
  directives: {},
  mixins    : [AppData, Auth, DataTable],
  props     : {
    data: {
      type   : [Array],
      default: () => []
    }
  },
  data() {
    return {
      selectedMachineGroup        : null,
      machineGroupAddDialogVisible: false,

      dataTable: {
        autoload     : false,
        socketCommand: {
          list: window.SocketCommand.System.Printers.Get
        },
        resize: {
          offset: 185
        },
        options: {
          serverSideEnabled: false,
          itemsPerPage     : -1
        }
      }
    }
  },
  computed: {
    MachineGroupTypeEnum() {
      return MachineGroupTypeEnum
    },
    machineGroupsHeaders() {
      return [
        {
          text    : '#',
          align   : 'left',
          value   : 'Id',
          sortable: false
        },
        {
          text    : this.$t('Common.Misc.Name'),
          align   : 'left',
          value   : 'Name',
          sortable: false
        },
        {
          text    : this.$t('Common.Misc.Desc'),
          align   : 'left',
          value   : 'Description',
          sortable: false
        },
        {
          text    : this.$tc('Settings.Machine.Title', 0),
          align   : 'center',
          value   : 'MachinesCount',
          sortable: false
        },
        {
          text    : this.$t('Common.Misc.Type'),
          align   : 'center',
          value   : 'Type',
          sortable: false
        },
        {
          text    : this.$t('Common.Misc.Status'),
          align   : 'left',
          value   : 'Status',
          sortable: false
        },
        {
          text    : '',
          align   : 'right',
          value   : 'Actions',
          sortable: false
        }
      ]
    }
  },
  watch: {
    data() {
      this.setDataInternal()
    }
  },
  beforeCreate() {
  },
  created() {
  },
  beforeMount() {
    this.$bus.$on(window.SocketCommand.Settings.Machine.Group.Save, this.onSaveMachineGroupResult)
    this.$bus.$on(window.SocketCommand.Settings.Machine.Group.Delete, this.onDeleteMachineGroupResult)
  },
  mounted() {
    this.setDataInternal()
  },
  beforeUpdate() {
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Settings.Machine.Group.Save, this.onSaveMachineGroupResult)
    this.$bus.$off(window.SocketCommand.Settings.Machine.Group.Delete, this.onDeleteMachineGroupResult)
  },
  destroyed() {
  },
  methods: {
    setDataInternal() {
      this.dataTable.loading = false
      this.dataTable.data = this.data || []
      this.dataTable.total = this.data?.length || 0
    },

    onAddMachineGroupClick() {
      this.machineGroupAddDialogVisible = true
      this.selectedMachineGroup = null
    },
    onSaveMachineGroupResult() {
      this.getData()
      this.selectedMachineGroup = null
    },

    onEditMachineGroupClick(item) {
      if (!item) return
      const payload = clone(item)
      this.selectedMachineGroup = payload
      this.machineGroupAddDialogVisible = true
    },

    onToggleMachineGroupStatus(item) {
      if (!item) return
      const payload = clone(item)
      window.callAS(window.SocketCommand.Settings.Machine.Group.Save, payload)
    },

    onDeleteMachineGroupClick(item) {
      if (!item) return
      this.$set(item, 'loading', true)
      const payload = {Id: item.Id}
      window.callAS(window.SocketCommand.Settings.Machine.Group.Delete, payload)
    },
    onDeleteMachineGroupResult() {
      this.getData()
    }
  }
}
</script>

<style scoped>
:deep(.machines-group .v-toolbar__content) {
  padding: 0;
}

:deep(.v-input--selection-controls.v-input--switch) {
  display: inline-block;
  position: relative !important;
  width: 75px !important;
}

:deep(.v-input--selection-controls.v-input--switch label) {
  font-size: 12px !important;
  padding-right: 0;
  margin: 0;
}
</style>
